import { HttpError, NetworkError, TimeoutError, apiRequest, authHeaders } from '@/shared/api';
import { env } from '@/shared/config';
import type { Page } from '@/shared/lib/usePagedQuery';
import type { SpeechPhone } from '@/shared/lib/talking-head';
import type {
  CorrectionItem,
  Dialogue,
  DialogueProfile,
  DialogueReport,
  DialogueScenario,
  DialogueSummary,
  LearnedFact,
  SpeechChunk,
  SpeechSink,
  TurnResponse,
} from '../model/types';

/** Ход модели укладывается в 1–2.5 с; 25 с — это уже не «долго», а «не будет». */
const TURN_TIMEOUT_MS = 25_000;

export function createDialogue(
  scenario: DialogueScenario,
  profile: DialogueProfile | undefined,
  signal?: AbortSignal,
  /** Карточка ролевой игры, с которой начат разговор — см. `entities/practice`. */
  practiceScenarioId?: number,
  /**
   * Собеседник, выбранный на `/tutor-select` перед этим конкретным
   * разговором. Отдельно от `profile`: тот целиком есть только во время
   * онбординга, а выбор тут возможен и после него — сервер накладывает это
   * поле поверх сохранённого профиля, не заменяя остальное (имя, интересы...).
   */
  tutorId?: string,
  /** Тема/подтема с рулетки (`widgets/topic-picker-entry`) — сервер резолвит их в whitelisted-строки для промпта. */
  topicId?: string,
  subtopicIndex?: number,
  /**
   * Слаг статьи лендинга, с которой пришли микрофоном: сервер начнёт разговор
   * с её тьютором и её первой репликой (см. `landingOpener` на бэкенде).
   */
  landing?: string,
): Promise<Dialogue> {
  return apiRequest<Dialogue>('/dialogues', {
    method: 'POST',
    body: { scenario, profile, practiceScenarioId, tutorId, topicId, subtopicIndex, landing },
    signal,
    timeoutMs: TURN_TIMEOUT_MS,
  });
}

/** Открыть существующий разговор по адресу `/chat/:id` — в том числе после F5. */
export function loadDialogue(id: string, signal?: AbortSignal): Promise<Dialogue> {
  return apiRequest<Dialogue>(`/dialogues/${encodeURIComponent(id)}`, { signal });
}

/** Ключ `useCachedQuery` для истории разговоров — общий для `HistoryPage` и домашнего виджета «Ваши диалоги», чтобы у них был один и тот же кеш. */
export const DIALOGUE_HISTORY_CACHE_KEY = 'dialogues:history';

/**
 * История разговоров — превью последней реплики каждого, самые свежие сверху,
 * страницами: `cursor` — `nextCursor` предыдущей страницы, `null` — первая.
 */
export function fetchDialogues(cursor: string | null = null, signal?: AbortSignal): Promise<Page<DialogueSummary>> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return apiRequest<Page<DialogueSummary>>(`/dialogues${query}`, { signal });
}

/**
 * Текстовая реплика. Ответ идёт потоком (NDJSON): куски голоса тьютора — в
 * `speech` по мере синтеза, часто раньше самого ответа, потому что сервер
 * озвучивает ответ по фразам, пока модель его дописывает. Промис
 * разрешается ответом (`turn`), а голос может ещё идти после — `speech.end()`
 * скажет, когда кусков больше не будет. Протокол — `sendTurn` на бэкенде.
 */
export function sendDialogueMessage(
  id: string,
  text: string,
  profile: DialogueProfile | undefined,
  speech: SpeechSink,
): Promise<TurnResponse> {
  return requestTurn(
    `/dialogues/${encodeURIComponent(id)}/messages`,
    { 'Content-Type': 'application/json' },
    JSON.stringify({ text, profile }),
    speech,
  );
}

/**
 * Голосовая реплика: тот же поток, что у текстовой. `Content-Type` с границей
 * multipart проставляет сам браузер.
 */
export function sendDialogueAudio(id: string, audio: Blob, speech: SpeechSink): Promise<TurnResponse> {
  const form = new FormData();
  form.append('file', audio, `speech.${audio.type.includes('mp4') ? 'mp4' : 'webm'}`);
  return requestTurn(`/dialogues/${encodeURIComponent(id)}/audio`, {}, form, speech);
}

/**
 * Правка последней реплики (распознавание ослышалось): тот же поток, что у
 * обычного хода, — тьютор отвечает заново на исправленный текст, прежние
 * реплика и ответ на сервере заменяются (см. `POST .../edit`).
 */
export function editDialogueMessage(
  id: string,
  messageId: number,
  text: string,
  speech: SpeechSink,
): Promise<TurnResponse> {
  return requestTurn(
    `/dialogues/${encodeURIComponent(id)}/messages/${messageId}/edit`,
    { 'Content-Type': 'application/json' },
    JSON.stringify({ text }),
    speech,
  );
}

type TurnEvent =
  | ({ type: 'speech'; audio: string | null } & Omit<SpeechChunk, 'audio'>)
  | ({ type: 'turn' } & TurnResponse)
  | { type: 'error'; status: number; error: string };

/**
 * Ход потоком: по строке JSON на событие. Мимо `apiRequest` — тот читает
 * ответ целиком. Таймаут — только до ответа: голос после него дочитывается
 * без ограничения, он уже в пути.
 */
async function requestTurn(
  path: string,
  headers: Record<string, string>,
  body: BodyInit,
  speech: SpeechSink,
): Promise<TurnResponse> {
  const url = `${env.backendUrl}${path}`;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TURN_TIMEOUT_MS);
  // Те же ошибки, что у `apiRequest`, — по ним `toUserMessage` пишет человеку понятное.
  const failure = (error: unknown) => (timedOut ? new TimeoutError(url) : new NetworkError(url, error));
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { ...authHeaders(), ...headers, Accept: 'application/x-ndjson' },
      body,
      signal: controller.signal,
    });
  } catch (error) {
    clearTimeout(timer);
    speech.end();
    throw failure(error);
  }
  if (!response.ok || !response.body) {
    clearTimeout(timer);
    speech.end();
    throw new HttpError(response.status, response.url, await response.text().catch(() => ''));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  return new Promise<TurnResponse>((resolve, reject) => {
    let answered = false;
    const handle = (event: TurnEvent) => {
      if (event.type === 'speech') {
        speech.push({ ...event, audio: event.audio === null ? null : base64ToBuffer(event.audio) });
      } else if (event.type === 'turn') {
        answered = true;
        clearTimeout(timer);
        resolve({ userMessage: event.userMessage, reply: event.reply });
      } else {
        // Ход упал на сервере уже после того, как поток начался: статус у потока 200.
        answered = true;
        reject(new HttpError(event.status, response.url, event.error));
      }
    };
    void (async () => {
      let pending = '';
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          pending += decoder.decode(value, { stream: true });
          for (let nl = pending.indexOf('\n'); nl >= 0; nl = pending.indexOf('\n')) {
            const line = pending.slice(0, nl).trim();
            pending = pending.slice(nl + 1);
            if (line) handle(JSON.parse(line) as TurnEvent);
          }
        }
        if (!answered) reject(new HttpError(502, response.url, 'Stream ended without a turn'));
      } catch (error) {
        if (!answered) reject(failure(error));
      } finally {
        clearTimeout(timer);
        speech.end();
      }
    })();
  });
}

function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** Озвучка реплики: аудио приходит телом ответа, поэтому тоже мимо apiRequest. */
export async function fetchSpeech(id: string, messageId: number): Promise<Blob> {
  const response = await fetch(
    `${env.backendUrl}/dialogues/${encodeURIComponent(id)}/messages/${messageId}/speech`,
    { headers: authHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(response.status, response.url, '');
  }
  return response.blob();
}

/**
 * Та же озвучка реплики плюс фонемы с таймингами — для губ 3D-аватара. Аудио
 * приходит в base64 внутри JSON: тайминги относятся ровно к этому звуку.
 */
export async function fetchSpeechWithPhones(
  id: string,
  messageId: number,
): Promise<{ audio: ArrayBuffer; phones: SpeechPhone[] }> {
  const response = await fetch(
    `${env.backendUrl}/dialogues/${encodeURIComponent(id)}/messages/${messageId}/speech?phones=1`,
    { headers: authHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(response.status, response.url, '');
  }
  const json = (await response.json()) as { audio: string; phones: SpeechPhone[] };
  return { audio: base64ToBuffer(json.audio), phones: json.phones };
}

/** Перевод целой реплики (тьютора или своей) на русский. */
export async function translateMessage(id: string, messageId: number): Promise<string> {
  const { text } = await apiRequest<{ text: string }>(
    `/dialogues/${encodeURIComponent(id)}/messages/${messageId}/translate`,
    { method: 'POST', timeoutMs: TURN_TIMEOUT_MS },
  );
  return text;
}

/** Разбор ошибок в своей реплике — по запросу, не вместе с сообщением. */
export async function explainMessageErrors(id: string, messageId: number): Promise<CorrectionItem[]> {
  const { items } = await apiRequest<{ items: CorrectionItem[] }>(
    `/dialogues/${encodeURIComponent(id)}/messages/${messageId}/errors`,
    { method: 'POST', timeoutMs: TURN_TIMEOUT_MS },
  );
  return items;
}

/** Более естественная формулировка своей реплики — тоже по запросу. */
export async function rephraseMessage(id: string, messageId: number): Promise<string> {
  const { phrase } = await apiRequest<{ phrase: string }>(
    `/dialogues/${encodeURIComponent(id)}/messages/${messageId}/rephrase`,
    { method: 'POST', timeoutMs: TURN_TIMEOUT_MS },
  );
  return phrase;
}

/** Перевод произвольного текста — например, предложенной перефразировки. */
export async function translateText(id: string, text: string): Promise<string> {
  const { text: translated } = await apiRequest<{ text: string }>(
    `/dialogues/${encodeURIComponent(id)}/translate`,
    { method: 'POST', body: { text }, timeoutMs: TURN_TIMEOUT_MS },
  );
  return translated;
}

/**
 * Озвучка произвольного текста — часть реплики или перефразировка, а не
 * привязанное к id сообщение (см. `fetchSpeech`).
 */
export async function fetchTextSpeech(id: string, text: string): Promise<Blob> {
  const response = await fetch(`${env.backendUrl}/dialogues/${encodeURIComponent(id)}/speech`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!response.ok) {
    throw new HttpError(response.status, response.url, await response.text().catch(() => ''));
  }
  return response.blob();
}

/**
 * Закрытие разговора — «в фоне»: пользователь уже ушёл, ждать его незачем.
 * Промис никогда не отклоняется — по нему только узнают, что сервер досчитал
 * визит (см. сброс кеша прогресса в `ChatScreen.exit`).
 */
export function endDialogue(id: string): Promise<void> {
  return apiRequest(`/dialogues/${encodeURIComponent(id)}/end`, { method: 'POST' }).then(
    () => undefined,
    () => {
      // Незакрытый разговор — не повод показывать ошибку на выходе из чата.
    },
  );
}

/**
 * Префиксы ключей кеша итогов разговора. Выход из чата придерживает их до
 * `/end` (см. `holdCacheUntil` в `ChatScreen.exit`): итоги, запрошенные
 * раньше, пришли бы без последнего визита.
 */
export const DIALOGUE_REPORT_CACHE_PREFIX = 'dialogue-report:';
export const DIALOGUE_LEARNED_CACHE_PREFIX = 'dialogue-learned:';

export function fetchDialogueReport(id: string): Promise<DialogueReport> {
  return apiRequest<DialogueReport>(`/dialogues/${encodeURIComponent(id)}/report`);
}

/**
 * Что тьютор узнал за разговор. Сервер отвечает, когда закончилось извлечение
 * фактов, — это вызов модели, поэтому таймаут длиннее обычного.
 */
export function fetchLearnedFacts(id: string): Promise<LearnedFact[]> {
  return apiRequest<{ facts: LearnedFact[] }>(`/dialogues/${encodeURIComponent(id)}/learned`, {
    timeoutMs: 30_000,
  }).then((response) => response.facts);
}

/** Свайп-удаление разговора из истории — реплики удаляются, зайти в чат снова нельзя. */
export function deleteDialogue(id: string, signal?: AbortSignal): Promise<void> {
  return apiRequest(`/dialogues/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
